"use client";

import React from "react";
import { Info, TrendingUp, Headset, ShieldCheck, ArrowRight, CircleCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const programDetails = [{
  title: "Heat Pump Grants",
  description: <>
        Eligible homeowners may qualify for SEAI heat pump grants worth up to €12,500, depending on the dwelling, heating system being replaced, required upgrades and current SEAI eligibility rules. For full details visit the{" "}
        <Link to="https://www.seai.ie/grants/home-energy-grants/individual-grants/heat-pump-systems" newTab className="text-primary underline font-medium hover:text-primary/80">
          SEAI Heat Pump Grants page
        </Link>
        . We handle all the technical SEAI paperwork to ensure your application is seamless.
      </>,
  icon: TrendingUp
}, {
  title: "Expert Guidance",
  description: "Our SEAI registered installers provide a full technical assessment of your home to determine the most efficient heating solution for your property.",
  icon: Headset
}, {
  title: "Full Compliance",
  description: "D4 Plumbing & Heating is fully insured and RGI registered. All installations are carried out to SEAI requirements.",
  icon: ShieldCheck
}];
const quickFeatures = ["SEAI Registered Installer", "Paperwork Handled by D4", "Technical Assessments", "Dublin-Wide Service"];
export default function SeaiGrantInformation() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: contentRef,
    isVisible: isContentVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="262809" id="seai-grant-information" className="py-16 md:py-24 bg-background text-foreground overflow-hidden">
      <div className="page-container">
        
        {}
        <Card className="mb-8 border-primary/20 bg-accent/40 shadow-sm">
          <CardContent className="p-4 sm:p-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-center sm:text-left">
            <div className="flex items-center gap-3">
              <Info className="w-5 h-5 text-primary shrink-0" />
              <span className="text-sm sm:text-base font-medium font-default text-foreground">
                For full details see our Heat Pump Installation Dublin page
              </span>
            </div>
            <Button asChild size="sm" variant="default" className="shrink-0 rounded-md">
              <Link to="/HeatPumpInstallationDublinPage">
                Heat Pump Installation Dublin
                <ArrowRight className="ml-1.5 w-4 h-4" />
              </Link>
            </Button>
          </CardContent>
        </Card>

        {}
        <div ref={headerRef} className={`max-w-3xl mx-auto text-center mb-12 md:mb-16 transition-all duration-700 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <Badge variant="secondary" className="mb-4 bg-accent text-accent-foreground font-mono uppercase tracking-wider">
            Financial Incentives
          </Badge>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-default font-bold tracking-tight mb-4 text-foreground">
            SEAI Grants for <span className="text-primary">Heat Pumps</span>
          </h2>
          <p className="text-lg text-muted-foreground font-default">
            Upgrading your home's efficiency is more affordable than ever. D4 Plumbing & Heating helps you navigate the grant process from start to finish.
          </p>
        </div>

        {}
        <div ref={contentRef} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center transition-all duration-1000 ease-out delay-200 ${isContentVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          
          {}
          <div className="order-2 lg:order-1 space-y-8">
            <div className="relative rounded-lg overflow-hidden shadow-xl border border-border group">
              <div className="absolute inset-0 bg-gradient-to-t from-primary/60 via-transparent to-transparent z-10 pointer-events-none" />
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689081/img/seai_grant_heat_pump.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689081/img/seai_grant_heat_pump.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689081/img/seai_grant_heat_pump.png-wvc-sizes" alt="Modern Heat Pump Installation Dublin" className="w-full h-auto object-cover aspect-[4/3] transition-transform duration-700 group-hover:scale-105" loading="lazy" />
              <div className="absolute bottom-0 left-0 right-0 p-6 z-20">
                <h3 className="text-white font-default font-semibold text-xl mb-2">
                  Grant Eligibility Checklist
                </h3>
                <p className="text-white/90 text-sm font-default">
                  Check if your Dublin home qualifies for maximum SEAI funding today.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {quickFeatures.map((feature, i) => <div key={i} data-index={i} className="flex items-center gap-3 bg-card p-3 rounded-md border border-border shadow-sm">
                  <CircleCheck className="w-5 h-5 text-primary shrink-0" />
                  <span className="text-sm font-medium font-default text-foreground">{feature}</span>
                </div>)}
            </div>
          </div>

          {}
          <div className="order-1 lg:order-2 flex flex-col gap-6">
            <div className="space-y-6">
              {programDetails.map((detail, i) => {
              const Icon = detail.icon;
              return <Card key={i} data-index={i} className="border-border shadow-md hover:shadow-lg transition-shadow bg-card backdrop-blur-sm">
                    <CardContent className="p-6 flex gap-5 items-start">
                      <div className="bg-primary/10 p-3 rounded-md shrink-0">
                        <Icon className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="text-xl font-default font-bold mb-2 text-foreground">
                          {detail.title}
                        </h3>
                        <div className="text-muted-foreground font-default leading-relaxed text-sm sm:text-base">
                          {detail.description}
                        </div>
                      </div>
                    </CardContent>
                  </Card>;
            })}
            </div>

            <div className="pt-6 mt-2 border-t border-border flex flex-col sm:flex-row items-center gap-4">
              <Button asChild size="lg" className="w-full sm:w-auto text-base h-12 px-8 shadow-lg bg-primary hover:bg-primary/90 rounded-md">
                <Link to="/HeatPumpsPage">
                  Check Grant Eligibility
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
              <p className="text-sm text-muted-foreground font-default text-center sm:text-left">
                D4 handles all the paperwork. <br className="hidden sm:block" />
                Professional Dublin installers.
              </p>
            </div>
          </div>

        </div>
      </div>
    </section>;
}